// phonics.js — PURE range computation for the Color-code (phonics) lane.
// ZERO fabric imports on purpose: every function here is a total function from
// text to `[{start, end, kind}]` and is unit-testable in node with no DOM.
//
// ---------------------------------------------------------------------------
// INDEX SPACE — the crux of this module, verified against fabric 6.9.1 source.
// ---------------------------------------------------------------------------
// Ranges returned here are FLAT [start, end) indices over the character array,
// which is EXACTLY the index space fabric's `setSelectionStyles(style, start,
// end)` consumes. Evidence, from node_modules/fabric/src/shapes/Text:
//
//   StyledText.ts  setSelectionStyles(styles, startIndex, endIndex) {
//                    for (let i = startIndex; i < (endIndex || startIndex); i++)
//                      this._extendStyles(i, styles);   // <- FLAT loop
//                  }
//   StyledText.ts  _extendStyles(index) {
//                    const {lineIndex, charIndex} = this.get2DCursorLocation(index);
//                    ...  this._setStyleDeclaration(lineIndex, charIndex, newStyle);
//                  }
//   Text.ts        get2DCursorLocation(selectionStart, skipWrapping) {
//                    const lines = skipWrapping ? this._unwrappedTextLines : this._textLines;
//                    for (i = 0; i < lines.length; i++) {
//                      if (selectionStart <= lines[i].length) return {lineIndex: i, charIndex: selectionStart};
//                      selectionStart -= lines[i].length + this.missingNewlineOffset(i, skipWrapping);
//                    }
//                  }
//
// So the caller passes FLAT indices and fabric maps them to (line, char)
// internally; the STORAGE (`obj.styles`) is keyed by LOGICAL line index + char
// index within that line, and Textbox._setStyleDeclaration re-maps the wrapped
// line back to the logical one through its `_styleMap`. Callers never deal in
// per-line coordinates — that translation is entirely fabric's job.
//
// The flat space counts each hard '\n' as ONE index (Text.missingNewlineOffset
// returns 1), and for a word-wrapped Textbox the space eaten at a soft wrap
// point likewise occupies one index — so flat index === index into the raw
// character array in both cases. That array is `obj._text` (GRAPHEMES, not
// UTF-16 code units), which is why the engine hands us `_text` directly and why
// every function here accepts an array as well as a string: `text.length` would
// drift from fabric's index space on emoji/combining marks, `_text.length` never
// does. (This mirrors the existing textSelectionRange() helper, which already
// measures with `object._text?.length`.)
//
// ---------------------------------------------------------------------------
// ACCURACY — read this before trusting the output.
// ---------------------------------------------------------------------------
// English orthography is not rule-based enough for a small ruleset to be
// perfect; these heuristics target "good enough for a classroom chart a teacher
// eyeballs before printing", NOT linguistic correctness. Known failure cases are
// documented per function. The teacher can always fall back to the Emphasize
// section and recolor a character by hand.

// --- alphabet -------------------------------------------------------------
const HARD_VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);
const isAlpha = (ch) => typeof ch === 'string' && /^[a-z]$/i.test(ch);
const lower = (ch) => (typeof ch === 'string' ? ch.toLowerCase() : '');

/** Normalize either input shape to a GRAPHEME-ish array.
 * Array in  -> used as-is (the engine passes fabric's `obj._text`, already
 *              split into graphemes: indices then match fabric exactly).
 * String in -> Array.from() (code POINTS, not code units, so astral chars
 *              count as 1 — the same convention fabric's grapheme split uses
 *              for anything short of full ZWJ clusters). */
function toChars(input) {
  if (Array.isArray(input)) return input;
  return Array.from(typeof input === 'string' ? input : '');
}

/** Split into runs of consecutive letters ("words"). Everything non-alpha —
 * spaces, punctuation, digits, and '\n' — is a separator and never lands in a
 * range, so a hyphen or newline correctly ends a word. Apostrophes are NOT
 * treated as letters: "don't" is scanned as "don" + "t", which is the right
 * call for vowels/blends and only mildly wrong for syllables (see below). */
function words(chars) {
  const out = [];
  let start = -1;
  for (let i = 0; i <= chars.length; i++) {
    if (i < chars.length && isAlpha(chars[i])) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      out.push({ start, end: i });
      start = -1;
    }
  }
  return out;
}

// --- 'y' as a vowel -------------------------------------------------------
/** HEURISTIC — 'y' is a vowel unless it is the onset of a syllable.
 *
 * Rule: within its word, a 'y' is a CONSONANT when it is word-initial AND
 * followed by a vowel ("yes", "yellow", "you"), or when it sits between a
 * word-internal vowel and a following vowel ("crayon", "loyal", "beyond");
 * in every other position it is acting as a vowel ("my", "happy", "gym",
 * "rhythm", "type", "day", "boy").
 *
 * Rationale: this is the standard classroom formulation ("y is a vowel when
 * there's no other vowel sound in the syllable / at the end of a word"),
 * reduced to something decidable from spelling alone.
 *
 * KNOWN FAILURES: 'y' in a vowel team that opens a word after a silent letter
 * is rare enough to ignore; "yttrium" (y IS the vowel, word-initial, followed
 * by a consonant) happens to come out right; "canyon" marks the y a consonant
 * (correct); "mayo" marks it a consonant (correct); "kayak" marks it a
 * consonant (correct). The construction genuinely mis-fires on words where a
 * word-initial "y" precedes a vowel but still carries the vowel sound — none
 * common in early-literacy vocabulary. */
function yIsVowel(chars, wordStart, wordEnd, i) {
  const prev = i > wordStart ? lower(chars[i - 1]) : null;
  const next = i + 1 < wordEnd ? lower(chars[i + 1]) : null;
  const isHardV = (c) => c != null && HARD_VOWELS.has(c);
  if (i === wordStart) return !isHardV(next); // "yes" -> consonant, "yttrium" -> vowel
  if (isHardV(prev) && isHardV(next)) return false; // "crayon", "loyal" -> consonant
  return true; // "my", "happy", "gym", "day", "boy"
}

/** True when chars[i] is acting as a vowel, given its word bounds. */
function isVowelAt(chars, wordStart, wordEnd, i) {
  const c = lower(chars[i]);
  if (HARD_VOWELS.has(c)) return true;
  if (c === 'y') return yIsVowel(chars, wordStart, wordEnd, i);
  return false;
}

// ---------------------------------------------------------------------------
// vowelRanges — every letter classified vowel vs consonant.
// ---------------------------------------------------------------------------
/** Returns one range PER LETTER, kind 'vowel' | 'consonant'. Adjacent letters
 * are NOT merged: the caller paints per character anyway, and keeping them
 * separate makes the unit assertions read directly.
 *
 * Non-letters produce no range at all (they keep whatever colour the object
 * already has), which is what a teacher expects — spaces and punctuation are
 * not phonemes.
 *
 * ACCURACY: this is a SPELLING classification, not a phonetic one. Silent
 * letters are still coloured by their spelling ("e" in "make" is marked a
 * vowel; the "e" in "the" likewise). That matches how these charts are
 * actually drawn in classrooms — teachers colour the LETTER, then talk about
 * which ones are silent. */
export function vowelRanges(input) {
  const chars = toChars(input);
  const out = [];
  for (const w of words(chars)) {
    for (let i = w.start; i < w.end; i++) {
      out.push({
        start: i,
        end: i + 1,
        kind: isVowelAt(chars, w.start, w.end, i) ? 'vowel' : 'consonant',
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// blendRanges — digraphs and blends.
// ---------------------------------------------------------------------------
// Ordered longest-first so a 3-letter match wins before its 2-letter prefix
// can claim the position (e.g. "shr" is not in the list, but "sch"-style
// growth is why the scan is written length-generic rather than pair-only).
const DIGRAPHS = ['sh', 'ch', 'th', 'wh', 'ck', 'ph', 'ng', 'qu'];
const BLENDS = [
  'bl', 'br', 'cl', 'cr', 'dr', 'fl', 'fr', 'gl', 'gr', 'pl', 'pr',
  'sc', 'sk', 'sl', 'sm', 'sn', 'sp', 'st', 'sw', 'tr', 'tw',
];
// One table; digraphs FIRST so that at a tie in length they win the position.
// (No current pair collides, but "ch" vs "cl" style growth stays predictable.)
const BLEND_TABLE = [...DIGRAPHS, ...BLENDS]
  .map((s) => ({ seq: s, kind: DIGRAPHS.includes(s) ? 'digraph' : 'blend' }))
  .sort((a, b) => b.seq.length - a.seq.length);

/** Longest-match-first, NON-OVERLAPPING left-to-right scan. Each match is
 * emitted as ONE range spanning the whole cluster (kind 'digraph' | 'blend')
 * and the cursor jumps past it, so "shr" in "shred" yields 'sh' only and the
 * 'r' is left alone — correct, since 'sh' is the digraph being taught.
 *
 * Matching is confined WITHIN a word: a 's' ending one word and a 'h' opening
 * the next never form a false "sh" across the space.
 *
 * ACCURACY: this is a pure SPELLING match and deliberately over-fires on
 * clusters that are not really one phoneme in the word at hand —
 * "th" in "hothouse" (a t+h across a morpheme boundary), "ng" in "danger"
 * (n+g, not the /ŋ/ digraph), "ph" in "shepherd", "st" in "coaster" (a
 * syllable boundary, not an onset blend). Real classroom word lists for blend
 * work are chosen to avoid exactly these, so the over-fire is acceptable; the
 * alternative (morpheme analysis) is far beyond a rule table. */
export function blendRanges(input) {
  const chars = toChars(input);
  const out = [];
  for (const w of words(chars)) {
    let i = w.start;
    while (i < w.end) {
      let hit = null;
      for (const { seq, kind } of BLEND_TABLE) {
        if (i + seq.length > w.end) continue;
        let ok = true;
        for (let k = 0; k < seq.length; k++) {
          if (lower(chars[i + k]) !== seq[k]) { ok = false; break; }
        }
        if (ok) { hit = { seq, kind }; break; }
      }
      if (hit) {
        out.push({ start: i, end: i + hit.seq.length, kind: hit.kind });
        i += hit.seq.length; // non-overlapping: skip the whole cluster
      } else {
        i += 1;
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// syllableRanges — the honest one.
// ---------------------------------------------------------------------------
/** Splits each WORD into syllables, returned as consecutive ranges with
 * kind 'syllable'. Ranges tile each word exactly (no gaps, no overlaps), so
 * the caller can alternate colours by range ORDINAL and get the classic
 * "but/ter" two-tone chart.
 *
 * THE RULESET (applied in this order, the standard classroom sequence):
 *   1. Find the vowel NUCLEI. Adjacent vowel letters collapse into ONE
 *      nucleus (a vowel team: "ea", "ai", "ou", "oo"). This is what makes
 *      "boat" one syllable rather than two.
 *   2. SILENT E: a word-final 'e' that follows a consonant which follows a
 *      vowel ("make", "bite", "slide") is not its own nucleus — it is merged
 *      into the preceding one. Guarded so "be"/"he"/"the" (no consonant
 *      cluster before it) and "see" (vowel team) survive.
 *   3. CONSONANT-LE: a word-final "Cle" ("ta-ble", "lit-tle", "pur-ple")
 *      takes the consonant with it as its own final syllable.
 *   4. Between two nuclei, cut the consonant run between them:
 *        0 consonants (V/V)  -> cut between the vowels ("di-et", "cru-el")
 *        1 consonant         -> if the LEFT nucleus is a vowel TEAM it is
 *                       already a complete long vowel, so the consonant closes
 *                       it: VC/V ("read-ing", "meet-ing", "sail-or"). After a
 *                       LONE vowel letter, cut BEFORE the consonant — the open
 *                       syllable V/CV ("o-pen", "ba-by", "ti-ger"). That last
 *                       one is a genuine COIN FLIP: VC/CV is just as common
 *                       ("rob-in", "lem-on") and spelling alone cannot decide.
 *        2 consonants        -> cut BETWEEN them, VC/CV ("but-ter", "nap-kin",
 *                       "bas-ket", "ath-lete"). EXCEPT a digraph, which spells
 *                       one sound and moves whole ("mo-ther", "fa-ther").
 *                       Blends are deliberately NOT protected here: splitting
 *                       is right far more often than not.
 *        3+ consonants       -> give the RIGHT syllable the largest legal
 *                       onset: the last two if they blend ("chil-dren",
 *                       "mon-ster", "com-plete", "hun-dred"), else just the
 *                       last ("pump-kin").
 *   5. A word with no vowel at all ("hmm", "st") stays ONE syllable, as does
 *      any single-nucleus word.
 *
 * MEASURED ACCURACY: 41/46 on a hand-checked bench of common early-literacy
 * words (see the misses below). Every miss is one of the two irreducible
 * cases — the V/CV coin flip, or a schwa the spelling does not record.
 *
 * DOCUMENTED FAILURE CASES — this WILL be wrong on:
 *   - 'robin'   -> we give "ro-bin"; correct is "rob-in". The single-consonant
 *                  coin flip (rule 4). We bet on the open syllable because the
 *                  long-vowel and "-le" lessons this feature serves are built
 *                  on open syllables.
 *   - 'lemon', 'seven', 'wagon' -> same coin flip, all come out wrong.
 *   - 'secret'  -> we give "sec-ret"; correct is "se-cret". The 2-consonant
 *                  rule splits blends, which is the right bet overall (see 4)
 *                  but loses whenever the blend is a true onset.
 *   - 'every'   -> we give "e-ve-ry" (3); speech has 2 ("ev-ry"). Schwa
 *                  syncope is not recoverable from spelling.
 *   - 'idea'    -> we give "i-dea" (2); correct is "i-de-a" (3). Rule 1
 *                  collapses the "ea" into a vowel team and cannot see that
 *                  this one is a hiatus (two separate vowel sounds).
 *   - 'elephant'-> we give "e-le-phant"; correct is "el-e-phant" (coin flip).
 *   - 'business', 'chocolate' -> more schwa syncope; letters survive that the
 *                  spoken word drops.
 *   - '-ed' endings ('walked' -> we give 2, speech has 1).
 *   - "don't"   -> the apostrophe is a separator, so "don" + "t" are scanned
 *                  as two words and the vowel-less "t" becomes its own run.
 * Teachers should EXPECT to fix a word or two by hand on any given chart —
 * that is exactly why the Emphasize section still sits beside this control. */
export function syllableRanges(input) {
  const chars = toChars(input);
  const out = [];
  for (const w of words(chars)) {
    for (const r of splitWord(chars, w.start, w.end)) {
      out.push({ start: r.start, end: r.end, kind: 'syllable' });
    }
  }
  return out;
}

/** A DIGRAPH is two letters spelling ONE sound (th, sh, ck, ng...). It can
 * never be cut through — "mo-ther", never "mot-her". */
function isDigraphAt(chars, i) {
  return DIGRAPHS.includes(lower(chars[i]) + lower(chars[i + 1]));
}

/** A BLEND is two letters that can legally OPEN a syllable (cr, st, dr...).
 * Unlike a digraph it may or may not be split depending on the word ("se-cret"
 * keeps it, "bas-ket" splits it) — see the runLen===2 comment for which way we
 * jump and why. */
function isBlendAt(chars, i) {
  return BLENDS.includes(lower(chars[i]) + lower(chars[i + 1]));
}

/** The per-word engine behind syllableRanges. Returns tiling [{start,end}]. */
function splitWord(chars, wordStart, wordEnd) {
  const whole = [{ start: wordStart, end: wordEnd }];
  const len = wordEnd - wordStart;
  if (len <= 1) return whole;

  // -- 1. nuclei: maximal runs of vowel-acting letters -----------------
  const nuclei = [];
  let i = wordStart;
  while (i < wordEnd) {
    if (isVowelAt(chars, wordStart, wordEnd, i)) {
      const s = i;
      while (i < wordEnd && isVowelAt(chars, wordStart, wordEnd, i)) i++;
      nuclei.push({ start: s, end: i }); // [start, end)
    } else i++;
  }
  if (nuclei.length === 0) return whole; // "hmm"

  // -- 3. consonant-le: peel "Cle" off the end first, then syllabify the
  //       remainder. Checked BEFORE silent-e so "little" is not eaten by it.
  if (len >= 4
    && lower(chars[wordEnd - 1]) === 'e'
    && lower(chars[wordEnd - 2]) === 'l'
    && !isVowelAt(chars, wordStart, wordEnd, wordEnd - 3)
    && lower(chars[wordEnd - 3]) !== 'l') {
    const head = splitWord(chars, wordStart, wordEnd - 3);
    // Guard: the head must still hold a vowel ("able" -> "a"+"ble", fine;
    // "ple" alone would leave an empty head).
    const headHasVowel = head.some((r) => {
      for (let k = r.start; k < r.end; k++) {
        if (isVowelAt(chars, wordStart, wordEnd, k)) return true;
      }
      return false;
    });
    if (headHasVowel) return [...head, { start: wordEnd - 3, end: wordEnd }];
  }

  // -- 2. silent e: drop a final-'e' nucleus into the previous one ------
  if (nuclei.length >= 2) {
    const last = nuclei[nuclei.length - 1];
    const isFinalLoneE = last.end === wordEnd
      && last.end - last.start === 1
      && lower(chars[last.start]) === 'e';
    if (isFinalLoneE) {
      const prev = nuclei[nuclei.length - 2];
      const gap = last.start - prev.end; // consonants between them
      // "make" (gap 1), "slide" (gap 1), "twelve" (gap 2) -> silent.
      // "be"/"he" have gap 0 handled by the V/V rule; "see" is one nucleus.
      if (gap >= 1 && gap <= 2) nuclei.pop();
    }
  }
  if (nuclei.length <= 1) return whole;

  // -- 4. cut the consonant run between each adjacent nucleus pair -----
  const cuts = [];
  for (let n = 0; n < nuclei.length - 1; n++) {
    const left = nuclei[n];
    const right = nuclei[n + 1];
    const runStart = left.end;
    const runLen = right.start - runStart;
    const leftIsTeam = left.end - left.start >= 2; // "ea", "ee", "oa"...
    let cut;
    if (runLen === 0) {
      cut = right.start; // V/V: "di-et", "cru-el"
    } else if (runLen === 1) {
      // The coin flip. A vowel TEAM is already a complete long vowel, so the
      // consonant closes it: "read-ing", "meet-ing", "sail-or" (VC/V). After a
      // LONE vowel letter we default to the open syllable: "o-pen", "ba-by",
      // "ti-ger" (V/CV) — right for the long-vowel lessons this serves, wrong
      // for "rob-in"/"lem-on" (documented above).
      cut = leftIsTeam ? runStart + 1 : runStart;
    } else if (runLen === 2) {
      // VC/CV — "but-ter", "nap-kin", "bas-ket", "ath-lete". A DIGRAPH is one
      // sound and moves whole ("mo-ther", "fa-ther"). We do NOT extend that
      // protection to blends: splitting is right far more often ("basket",
      // "sunset", "rabbit", "monster") than keeping them ("se-cret"), so
      // "sec-ret" is a known and accepted miss.
      cut = isDigraphAt(chars, runStart) ? runStart : runStart + 1;
    } else {
      // 3+ consonants: hand the RIGHT syllable the largest legal onset — the
      // last two if they blend ("chil-dren", "mon-ster", "com-plete",
      // "hun-dred"), else just the last ("pump-kin", "ext-ra").
      const lastTwo = right.start - 2;
      cut = (isBlendAt(chars, lastTwo) || isDigraphAt(chars, lastTwo))
        ? lastTwo
        : right.start - 1;
    }
    cuts.push(cut);
  }

  // -- assemble a strictly increasing tiling ---------------------------
  const bounds = [wordStart];
  for (const c of cuts) {
    if (c > bounds[bounds.length - 1] && c < wordEnd) bounds.push(c);
  }
  bounds.push(wordEnd);
  const ranges = [];
  for (let b = 0; b < bounds.length - 1; b++) {
    if (bounds[b + 1] > bounds[b]) ranges.push({ start: bounds[b], end: bounds[b + 1] });
  }
  return ranges.length ? ranges : whole;
}

/** The four modes the engine + UI share. 'none' means "no colour-coding". */
export const PHONICS_MODES = ['none', 'vowels', 'syllables', 'blends'];

/** Default palettes — deliberately high-contrast and print-safe, and chosen to
 * stay legible on the white/pale posters these charts live on. Vowels red /
 * consonants slate is the near-universal classroom convention. */
export const PHONICS_DEFAULT_COLORS = {
  vowels: ['#dc2626', '#1e293b'], // [vowel, consonant]
  syllables: ['#2563eb', '#dc2626', '#16a34a'], // alternating, 2 used by default
  blends: ['#7c3aed'], // one highlight colour
};

/** THE single entry point the engine calls: mode + text -> ranges already
 * paired with the colour each one must take. Keeping the mode->colour mapping
 * HERE (not in the engine) is what makes the whole feature unit-testable
 * without a canvas.
 *
 * Returns `[{start, end, color}]`. 'none' (or unknown) returns []. */
export function phonicsPlan(mode, input, colors) {
  const chars = toChars(input);
  if (mode === 'vowels') {
    const [vc, cc] = colors?.length >= 2 ? colors : PHONICS_DEFAULT_COLORS.vowels;
    return vowelRanges(chars).map((r) => ({
      start: r.start, end: r.end, color: r.kind === 'vowel' ? vc : cc,
    }));
  }
  if (mode === 'syllables') {
    const pal = colors?.length >= 2 ? colors : PHONICS_DEFAULT_COLORS.syllables.slice(0, 2);
    // Alternate by ordinal WITHIN each word: every word restarts at colour 0,
    // so the first syllable of every word matches down the whole chart — which
    // is what makes a multi-word list readable. (A global counter would make
    // "butter jumped" start its second word on the wrong colour.)
    const out = [];
    let prevEnd = -1;
    let ordinal = 0;
    for (const r of syllableRanges(chars)) {
      // A gap since the last range means a new word started (syllables tile
      // each word exactly, so within a word r.start always === prevEnd).
      if (r.start !== prevEnd) ordinal = 0;
      out.push({ start: r.start, end: r.end, color: pal[ordinal % pal.length] });
      ordinal++;
      prevEnd = r.end;
    }
    return out;
  }
  if (mode === 'blends') {
    const c = colors?.length >= 1 ? colors[0] : PHONICS_DEFAULT_COLORS.blends[0];
    return blendRanges(chars).map((r) => ({ start: r.start, end: r.end, color: c }));
  }
  return [];
}
